// src/components/complaint/MyComplaints.tsx
import { useState, useEffect } from "react";
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next'; 
import {
  Plus, Search, Clock, CheckCircle, XCircle, AlertCircle,
  MessageSquare, ChevronRight, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { getMyComplaints } from '../../../services/complaint.service';
import { Seo } from "../../components/Seo";

const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

function MyComplaintsSkeleton() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div><Skeleton className="h-8 w-52 mb-2" /><Skeleton className="h-4 w-64" /></div>
        <Skeleton className="h-11 w-40 rounded-xl" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[1,2,3,4].map(i => (
          <div key={i} className="bg-white rounded-2xl border border-[#E5E7EB] p-4">
            <Skeleton className="h-8 w-10 mb-2" /><Skeleton className="h-4 w-20" />
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border-b border-[#E5E7EB]">
          <div className="flex gap-2">{[1,2,3,4].map(i => <Skeleton key={i} className="h-9 w-20 rounded-xl" />)}</div>
          <div className="sm:ml-auto"><Skeleton className="h-10 w-56 rounded-xl" /></div>
        </div>
        <div className="hidden md:block">
          <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 px-5 py-3 border-b border-[#E5E7EB]">
            {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-4 w-14" />)}
          </div>
          {[1,2,3,4,5].map(row => (
            <div key={row} className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 items-center px-5 py-4 border-b border-[#F3F4F6]">
              <Skeleton className="h-4 w-24" />
              <div><Skeleton className="h-4 w-44 mb-2" /><Skeleton className="h-3 w-28" /></div>
              <Skeleton className="h-6 w-28 rounded-lg" />
              <Skeleton className="h-6 w-24 rounded-full" />
              <Skeleton className="h-4 w-12" />
            </div>
          ))}
        </div>
        <div className="md:hidden divide-y divide-[#E5E7EB]">
          {[1,2,3].map(card => (
            <div key={card} className="p-4">
              <div className="flex justify-between mb-3">
                <div><Skeleton className="h-4 w-40 mb-2" /><Skeleton className="h-3 w-28" /></div>
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>
              <div className="flex gap-2"><Skeleton className="h-5 w-28 rounded-lg" /><Skeleton className="h-4 w-20" /></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ t }: { t: any }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-14 h-14 bg-[#F5F7FA] rounded-full flex items-center justify-center mb-4">
        <FileText className="w-7 h-7 text-[#BFC2C7]" />
      </div>
      <p className="text-[#2D323A] mb-1">{t('complaints.no_results')}</p>
      <p className="text-sm text-[#717182] mb-5">{t('complaints.no_results_desc')}</p>
      <Link to="/complaints/new" className="flex items-center gap-2 px-5 py-2.5 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors text-sm">
        <Plus className="w-4 h-4" /> {t('complaints.lodge_complaint')}
      </Link>
    </div>
  );
}

const TABS = [
  { key: 'all', labelKey: 'tabs.all' },
  { key: 'open', labelKey: 'tabs.open', statuses: ['submitted', 'under_review', 'awaiting_response'] },
  { key: 'resolved', labelKey: 'tabs.resolved', statuses: ['resolved'] },
  { key: 'closed', labelKey: 'tabs.closed', statuses: ['closed', 'rejected'] },
];

export function MyComplaints() {
  const { t } = useTranslation('auth'); 
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [complaints, setComplaints] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ total_records: 0, total_pages: 0, page_number: 1, page_size: 10 });

  // Status config with translations
  const getStatusConfig = (status: string) => {
    const configs: Record<string, { label: string; color: string; bg: string }> = {
      submitted: { label: t('complaints.status.submitted'), color: 'text-[#1E6DFF]', bg: 'bg-[#1E6DFF]/10' },
      under_review: { label: t('complaints.status.under_review'), color: 'text-[#F59E0B]', bg: 'bg-[#F59E0B]/10' },
      awaiting_response: { label: t('complaints.status.awaiting_response'), color: 'text-[#8B5CF6]', bg: 'bg-[#8B5CF6]/10' },
      resolved: { label: t('complaints.status.resolved'), color: 'text-[#22C55E]', bg: 'bg-[#22C55E]/10' },
      rejected: { label: t('complaints.status.rejected'), color: 'text-[#EF4444]', bg: 'bg-[#EF4444]/10' },
      closed: { label: t('complaints.status.closed'), color: 'text-[#717182]', bg: 'bg-[#717182]/10' },
    };
    return configs[status] || configs.submitted;
  };

  const fetchComplaints = async (page = 1) => {
    try {
      setLoading(true);
      const status = tab !== 'all' ? tab : undefined;
      const res = await getMyComplaints({ page, pageSize: 10, status, search: search || undefined });
      if (res.success) {
        setComplaints(res.data);
        setPagination(res.pagination);
      } else {
        toast.error(res.message || t('complaints.messages.load_failed'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('complaints.messages.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComplaints(1);
  }, [tab, search]);

  const counts = {
    all: pagination.total_records,
    open: complaints.filter(c => ['submitted', 'under_review', 'awaiting_response'].includes(c.status)).length,
    resolved: complaints.filter(c => c.status === 'resolved').length,
    closed: complaints.filter(c => ['closed', 'rejected'].includes(c.status)).length,
  };

  if (loading && complaints.length === 0) {
    return <MyComplaintsSkeleton />;
  }

  return (
    <>
    <Seo
  title={t('complaints.seo.title')}
  description={t('complaints.seo.description')}
/>
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl text-[#2D323A]">{t('complaints.title')}</h1>
          <p className="text-[#717182] text-sm">{t('complaints.subtitle')}</p>
        </div>
        <Link
          to="/complaints/new"
          className="flex items-center gap-2 px-5 py-2.5 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md text-sm"
        >
          <Plus className="w-4 h-4" /> {t('complaints.new_complaint')}
        </Link>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: t('complaints.stats.total'), value: counts.all, color: 'text-[#2D323A]', bg: 'bg-[#F5F7FA]' },
          { label: t('complaints.stats.open'), value: counts.open, color: 'text-[#F59E0B]', bg: 'bg-[#F59E0B]/10' },
          { label: t('complaints.stats.resolved'), value: counts.resolved, color: 'text-[#22C55E]', bg: 'bg-[#22C55E]/10' },
          { label: t('complaints.stats.closed'), value: counts.closed, color: 'text-[#717182]', bg: 'bg-[#717182]/10' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-2xl p-4 border border-[#E5E7EB]`}>
            <p className={`text-2xl ${s.color}`}>{s.value}</p>
            <p className="text-sm text-[#717182]">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border-b border-[#E5E7EB]">
          <div className="flex gap-1">
            {TABS.map(tabItem => (
              <button
                key={tabItem.key}
                onClick={() => { setTab(tabItem.key); setLoading(true); }}
                className={`px-4 py-2 rounded-xl text-sm transition-colors ${tab === tabItem.key ? 'bg-[#1E6DFF] text-white' : 'text-[#717182] hover:bg-[#F5F7FA]'}`}
              >
                {t(`complaints.${tabItem.labelKey}`)} <span className="ml-1 text-xs">({tabItem.key === 'all' ? pagination.total_records : counts[tabItem.key as keyof typeof counts]})</span>
              </button>
            ))}
          </div>
          <div className="sm:ml-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#717182]" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('search_placeholder')}
              className="pl-9 pr-4 py-2 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl text-sm focus:outline-none focus:border-[#1E6DFF] w-56"
            />
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block">
          <div className="grid grid-cols-[120px_minmax(0,1fr)_150px_140px_80px] gap-4 px-5 py-3 bg-[#F5F7FA] border-b border-[#E5E7EB] text-xs text-[#717182] items-center">
          <span>{t('complaints.table.reference')}</span>
          <span>{t('complaints.table.subject')}</span>
          <span>{t('complaints.table.category')}</span>
          <span>{t('complaints.table.status')}</span>
          <span>{t('complaints.table.view')}</span>
        </div>
          {complaints.length === 0 ? (
            <EmptyState t={t} />
          ) : (
            <div className="divide-y divide-[#E5E7EB]">
              {complaints.map(c => {
                const s = getStatusConfig(c.status);
                return (
                  <div key={c.id} className="grid grid-cols-[120px_minmax(0,1fr)_150px_140px_80px] gap-4 items-center px-5 py-4 hover:bg-[#F5F7FA] transition-colors">
                    <span className="text-xs text-[#717182] ">{c.referenceNumber}</span>
                    <div>
                      <p className="text-sm text-[#2D323A]">{c.subject}</p>
                      <p className="text-xs text-[#717182]">{c.sellerName || t('complaints.table.no_seller')}</p>
                    </div>
                    <span className="text-xs px-2 py-1 bg-[#F5F7FA] text-[#717182] rounded-lg border border-[#E5E7EB] whitespace-nowrap">{c.category}</span>
                    <span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${s.bg} ${s.color}`}>{s.label}</span>
                    <Link to={`/complaints/${c.id}`} className="flex items-center gap-1 text-xs text-[#1E6DFF] hover:underline whitespace-nowrap">
                      {t('complaints.table.view')} <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y divide-[#E5E7EB]">
          {complaints.length === 0 ? <EmptyState t={t} /> : (
            complaints.map(c => {
              const s = getStatusConfig(c.status);
              return (
                <Link key={c.id} to={`/complaints/${c.id}`} className="block p-4 hover:bg-[#F5F7FA] transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm text-[#2D323A]">{c.subject}</p>
                      <p className="text-xs text-[#717182]">{c.referenceNumber} · {c.sellerName || ''}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${s.bg} ${s.color}`}>{s.label}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs px-2 py-0.5 bg-[#F5F7FA] text-[#717182] rounded-lg border border-[#E5E7EB]">{c.category}</span>
                    <span className="text-xs text-[#717182]">{new Date(c.createdAt).toLocaleDateString()}</span>
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </div>
    </div>
    </>
  );
}
